/**
 * Promote the source out of a statistic's label.
 *
 * Every stat callout in the corpus names its source, but names it as prose
 * inside the claim: "…entity understanding is how Google handles novel queries
 * (Google, 2024)". That reads as an afterthought and, more practically, it
 * cannot be styled, checked, or spotted by a reader skimming for whether the
 * number is trustworthy.
 *
 * For a blog whose whole subject is which sources get cited, the attribution
 * should be a visible, consistent part of the component rather than something
 * buried in a sentence. This pulls a trailing parenthetical into its own
 * element so the CSS can set it as a citation line.
 *
 * Pure string work, applied at render time to content already published, and
 * emitted directly by the content pipeline for anything published from now on.
 */

/** Class the extracted attribution is wrapped in. */
export const STAT_SOURCE_CLASS = "stat-source";

const STAT_LABEL_RE = /<span class="stat-label">([\s\S]*?)<\/span>/gi;

const YEAR_RE = /\b(?:19|20)\d{2}\b/;

/**
 * Split a label into its claim and a trailing attribution.
 *
 * An attribution is a parenthetical that closes the label and whose text names
 * a year: "(Gartner, 2024)" qualifies, "(and there are many)" does not, and
 * neither does a parenthetical mid-sentence.
 *
 * The parens are matched by counting rather than by a character class, because
 * the attribution is often a link and the link carries an inline style — so
 * the group legitimately contains `rgba(255,255,255,0.6)`. Excluding
 * parentheses outright missed exactly those, which are the well-cited ones.
 */
function splitAttribution(label: string): { claim: string; source: string } | null {
  const trimmed = label.trimEnd();
  if (!trimmed.endsWith(")")) return null;

  let depth = 0;
  let open = -1;
  for (let i = trimmed.length - 1; i >= 0; i--) {
    const ch = trimmed[i];
    if (ch === ")") depth++;
    else if (ch === "(") {
      depth--;
      if (depth === 0) { open = i; break; }
    }
  }
  if (open <= 0) return null;

  const inner = trimmed.slice(open + 1, -1);
  // Test the visible text, not the markup: a year inside an href is not a date.
  if (!YEAR_RE.test(inner.replace(/<[^>]+>/g, " "))) return null;

  const claim = trimmed.slice(0, open).trimEnd();
  if (!claim) return null;
  return { claim, source: inner.trim() };
}

/**
 * Move a trailing "(Source, Year)" out of each stat label into its own span.
 *
 * Idempotent: a label whose source has already been extracted no longer ends
 * in a parenthetical, so a second pass is a no-op. Labels with no recognizable
 * attribution are left exactly as they are — inventing one would be worse than
 * showing none.
 */
export function promoteStatSources(html: string): string {
  return html.replace(STAT_LABEL_RE, (whole, label: string) => {
    const split = splitAttribution(label);
    if (!split) return whole;
    return (
      `<span class="stat-label">${split.claim}</span>` +
      `<span class="${STAT_SOURCE_CLASS}">${split.source}</span>`
    );
  });
}

/** Statistics in the HTML, for reporting on how well sourced an article is. */
export function statSources(html: string): { value: string; source: string }[] {
  const out: { value: string; source: string }[] = [];
  const blocks = html.matchAll(/<div class="stat-callout">([\s\S]*?)<\/div>/gi);
  for (const [, block] of blocks) {
    const value = block.match(/<span class="stat-number">([\s\S]*?)<\/span>/i)?.[1] ?? "";
    const label = block.match(/<span class="stat-label">([\s\S]*?)<\/span>/i)?.[1] ?? "";
    const promoted = block.match(new RegExp(`<span class="${STAT_SOURCE_CLASS}">([\\s\\S]*?)</span>`, "i"))?.[1];
    const trailing = splitAttribution(label)?.source;
    out.push({
      value: value.replace(/<[^>]+>/g, "").trim(),
      source: (promoted ?? trailing ?? "").replace(/<[^>]+>/g, "").trim(),
    });
  }
  return out;
}
